import { countCodePoints } from "@common-ui/utilities/characterCount";
import { defaultOverflowMessage } from "@/helpers/characterLimitMessage";

/**
 * LSP-20508: the single expansion table the settings message templates are MEASURED with.
 *
 * A template is stored with placeholder tokens (`${user}`, `${organization}`, …) but is SENT with
 * those tokens replaced, so the string that has to fit the 500-character limit is the expanded one,
 * not the one the admin typed. `validateTemplateLength` (helpers/utilities.js) has always judged the
 * expanded length; the field's counter measured the raw text, so repeating a mention pushed the
 * expansion over the limit while the counter still read comfortably under it and the save was
 * refused with no visible reason ("147 / 500" next to "Please enter a shorter template message").
 *
 * Exporting the expansion as a STRING — rather than as the per-token length deltas it used to be
 * arithmetic over — is what lets the counter and the rule share it: `useCharacterLimit`'s `countAs`
 * prop runs this over the value before BOTH the counter and the limit rule measure it, so the number
 * an admin sees is the number that blocks their save.
 *
 * Expansion uses the RESOLVED values for this organization and user — the same map the placeholder
 * chips and `CallMessagePreview` display — rather than the worst case a token could ever reach. The
 * first cut of this ticket used the worst case (91 characters for `${user}`), which made
 * `Ankit Patel ${user}` count 103 next to a chip reading `${user} -> Ankit Patel`; a counter that
 * contradicts the substitution shown on the same screen is the bug this ticket exists to remove.
 *
 * KNOWN CEILING: the template is stored on the organization and can be sent by any team member, so a
 * colleague with a longer name can still expand past the limit at send time even though the editing
 * admin saw a number within it. The old worst-case bound covered that at the cost of being wrong on
 * every screen it was shown on. The server re-checks the real expansion on send.
 */

/**
 * Every placeholder the settings templates support. Counting is driven by the caller's resolved
 * values (below), not by a table of guesses, so this only has to enumerate the token names.
 */
const PLACEHOLDER_PATTERN = /\$\{(?:organization|user|teamMember|day|date|time|timezone|scheduledDate)\}/g;

/** The resolved value each placeholder will be replaced with, as `getTemplatePlaceholderExamples` returns it. */
export type PlaceholderValues = Record<string, string>;

/**
 * Returns the template with every known placeholder replaced by a stand-in of the length that
 * placeholder ACTUALLY resolves to for this organization and user — the same values the chips under
 * the field advertise (`${user} -> Ankit Patel`) and `CallMessagePreview` renders.
 *
 * LSP-20508 first measured the worst case a placeholder could ever expand to (91 characters for
 * `${user}`, "firstname lastname" at 45 + space + 45). That made `Ankit Patel ${user}` count 103 while
 * the page was telling the admin, two lines below, that `${user}` is "Ankit Patel" — 23 characters in
 * total. A counter that contradicts the substitution the same screen is displaying is the same class of
 * bug this ticket exists to remove, just pointed the other way, so the resolved values win.
 *
 * Only ever measured, never displayed, so each value becomes a run of plain ASCII of its own code-point
 * length rather than the value itself. That is deliberate: the counting modes trim AFTER this transform
 * runs, so substituting a value that is itself padded (an organization named "  Acme  ") would let that
 * padding be trimmed away when the placeholder sits at the start or end of the template, counting less
 * than the rule does. Filler cannot be trimmed, so the two agree everywhere.
 *
 * A token with no resolved value contributes nothing, and an unknown or malformed token is left exactly
 * as typed — `containsInvalidPlaceholders` is the rule that rejects those.
 */
export function expandPlaceholdersForCounting(template: unknown, placeholderValues: PlaceholderValues = {}): string {
	const text = typeof template === "string" ? template : String(template ?? "");
	return text.replace(PLACEHOLDER_PATTERN, (token) => {
		const resolved = placeholderValues?.[token];
		return "x".repeat(countCodePoints(typeof resolved === "string" ? resolved : ""));
	});
}

export function templateOverflowMessage(
	maxLength: number,
	fieldNoun = "template message",
	template: unknown = "",
	placeholderValues: PlaceholderValues = {}
): string {
	const rawText = typeof template === "string" ? template : String(template ?? "");
	const rawLength = countCodePoints(rawText);
	// Placeholders are the CAUSE only when the text would have fitted without them. A template that is
	// already over on its own raw length is not fixed by removing a placeholder — telling the admin to
	// remove one would send them down a road that cannot end at a saved template (codex review).
	const placeholdersCausedTheOverflow =
		rawLength <= maxLength && countCodePoints(expandPlaceholdersForCounting(rawText, placeholderValues)) > rawLength;
	return placeholdersCausedTheOverflow
		? `Placeholders are counted at the longest value they can be replaced with, which puts this ${fieldNoun} over ${maxLength} characters. Remove a placeholder or shorten the text around it.`
		: defaultOverflowMessage(maxLength, fieldNoun, rawText);
}

/**
 * The string a settings message template is MEASURED as — the one the field's counter shows and the
 * one its limit rule judges.
 *
 * A template has to fit 500 characters TWICE over, and both caps are real:
 *   - RAW, because it is stored in `Organizations.notificationTemplate*`, a
 *     `DataTypes.STRING(MAX_NOTIFICATION_TEMPLATE_LENGTH)` varchar (apps/contact-api/src/db/models/
 *     organization.ts). This is the cap LSP-17482 deliberately routed the save through the field's own
 *     rule to keep, after finding a raw-over-limit template could save whenever expansion SHRANK it
 *     (`${timezone}` is 11 characters and expands to at most 5).
 *   - EXPANDED with the resolved values, because that is what is actually sent.
 * Whichever is longer is the one that binds, so that is the one measured. For a template with no
 * placeholders, or with only placeholders that grow, this is just the expansion — and for the
 * shrinking case it is the raw text, exactly the number the counter showed before this ticket.
 */
export function countableTemplateText(template: unknown, placeholderValues: PlaceholderValues = {}): string {
	const text = typeof template === "string" ? template : String(template ?? "");
	const expanded = expandPlaceholdersForCounting(text, placeholderValues);
	return countCodePoints(expanded) >= countCodePoints(text) ? expanded : text;
}
